// Schemas for routes-campaigns.ts (browser-safe zod). Contact import (WA-07) and segmented campaigns (WA-08).
// PRD §3, §6 (E.164, ambiguous matches → review), §13 (send eligibility, operating hours), §17 (P1 campaigns), A25.
import { z } from 'zod';
import { PermissionStatusSchema, type PermissionStatus } from './enums.js';
import { UsageBudgetStateSchema } from './api-usage.js';

const Id = z.string().min(1).max(64);
const Iso = z.string();
const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'Usa el formato HH:MM');
const intParam = (def: number, min: number, max: number) => z.coerce.number().int().min(min).max(max).default(def);

// ═════════════════════════ Contact import (WA-07) ═════════════════════════

/** Upload formats: CSV, or .xlsx (first sheet). Legacy .xls is refused (save as .xlsx or CSV). */
export const CONTACT_IMPORT_ACCEPT = '.csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const CONTACT_IMPORT_STATUSES = ['uploaded', 'previewed', 'applying', 'applied', 'failed', 'cancelled'] as const;
export const ContactImportStatusSchema = z.enum(CONTACT_IMPORT_STATUSES);
export type ContactImportStatus = z.infer<typeof ContactImportStatusSchema>;
export const CONTACT_IMPORT_STATUS_LABELS: Record<ContactImportStatus, string> = {
  uploaded: 'Archivo cargado',
  previewed: 'Vista previa lista',
  applying: 'Importando',
  applied: 'Importado',
  failed: 'Falló',
  cancelled: 'Cancelado',
};

/** Logical fields a CSV column can be mapped to. */
export const IMPORT_FIELDS = [
  'name',
  'phone',
  'email',
  'projectExternalId',
  'projectName',
  'stage',
  'source',
  'campaignId',
  'whatsappPermission',
  'whatsappEvidence',
  'voicePermission',
  'timezone',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];
export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  name: 'Nombre',
  phone: 'Teléfono',
  email: 'Correo',
  projectExternalId: 'Proyecto (id externo)',
  projectName: 'Proyecto (nombre)',
  stage: 'Etapa',
  source: 'Fuente',
  campaignId: 'Campaña de origen',
  whatsappPermission: 'Permiso de WhatsApp',
  whatsappEvidence: 'Evidencia del permiso',
  voicePermission: 'Permiso de llamadas',
  timezone: 'Zona horaria',
};

const ColumnRef = z.string().min(1).max(200).nullable().default(null);

export const ContactImportMappingSchema = z.object({
  columns: z.object(Object.fromEntries(IMPORT_FIELDS.map((f) => [f, ColumnRef])) as Record<ImportField, typeof ColumnRef>),
  /** Destination when a row has no project column (or it is empty). null = the row needs its own project. */
  defaultProjectId: Id.nullable().default(null),
  /**
   * Permission is only recorded from a mapped permission column AND when the importer confirms how the consent was
   * obtained (stored as evidence). An imported phone number alone is never permission (PRD §13).
   */
  consent: z
    .object({
      whatsappConfirmed: z.boolean().default(false),
      voiceConfirmed: z.boolean().default(false),
      sourceDescription: z.string().trim().max(300).nullable().default(null),
    })
    .default({ whatsappConfirmed: false, voiceConfirmed: false, sourceDescription: null }),
  markAsTest: z.boolean().default(false),
});
export type ContactImportMapping = z.infer<typeof ContactImportMappingSchema>;

export const IMPORT_PERMISSION_LABELS: Record<PermissionStatus, string> = { granted: 'Otorgado', denied: 'Negado', revoked: 'Revocado', unknown: 'Desconocido' };

export const IMPORT_ROW_OUTCOMES = ['create', 'update', 'invalid', 'duplicate', 'ambiguous'] as const;
export const ImportRowOutcomeSchema = z.enum(IMPORT_ROW_OUTCOMES);
export type ImportRowOutcome = z.infer<typeof ImportRowOutcomeSchema>;
export const IMPORT_ROW_OUTCOME_LABELS: Record<ImportRowOutcome, string> = {
  create: 'Contacto nuevo',
  update: 'Contacto existente (se completa)',
  invalid: 'Inválido',
  duplicate: 'Duplicado en el archivo',
  ambiguous: 'Coincidencia ambigua (revisión)',
};

export const ContactImportRowSchema = z.object({
  /** Line number in the file (the header is line 1). */
  line: z.number().int(),
  name: z.string().nullable(),
  phoneOriginal: z.string().nullable(),
  phoneE164: z.string().nullable(),
  email: z.string().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  stage: z.string().nullable(),
  outcome: ImportRowOutcomeSchema,
  reasons: z.array(z.string()),
  whatsappPermission: PermissionStatusSchema,
  matchedContactId: z.string().nullable(),
});
export type ContactImportRow = z.infer<typeof ContactImportRowSchema>;

export const ContactImportSummarySchema = z.object({
  total: z.number().int(),
  create: z.number().int(),
  update: z.number().int(),
  invalid: z.number().int(),
  duplicate: z.number().int(),
  ambiguous: z.number().int(),
  permission: z.object({ granted: z.number().int(), denied: z.number().int(), unknown: z.number().int() }),
  byProject: z.array(z.object({ projectId: z.string(), projectName: z.string(), count: z.number().int() })),
  byReason: z.array(z.object({ reason: z.string(), count: z.number().int() })),
  defaultCountry: z.string(),
});
export type ContactImportSummary = z.infer<typeof ContactImportSummarySchema>;

export const ContactImportResultSchema = z.object({
  processedRows: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  permissionsRecorded: z.number().int(),
  finishedAt: Iso.nullable(),
});

export const ContactImportSchema = z.object({
  id: Id,
  status: ContactImportStatusSchema,
  fileName: z.string(),
  rowCount: z.number().int(),
  headers: z.array(z.string()),
  delimiter: z.string(),
  encoding: z.string(),
  suggestedMapping: ContactImportMappingSchema,
  mapping: ContactImportMappingSchema.nullable(),
  summary: ContactImportSummarySchema.nullable(),
  result: ContactImportResultSchema.nullable(),
  error: z.string().nullable(),
  createdAt: Iso,
  appliedAt: Iso.nullable(),
  /** Limits applied to uploads (config). */
  limits: z.object({ maxRows: z.number().int(), maxBytes: z.number().int() }),
});
export type ContactImport = z.infer<typeof ContactImportSchema>;

export const UploadContactImportFieldsSchema = z.object({}).passthrough();

export const ContactImportPreviewSchema = z.object({
  import: ContactImportSchema,
  rows: z.array(ContactImportRowSchema),
  rowsTotal: z.number().int(),
});

export const ContactImportRowsQuerySchema = z.object({
  outcome: ImportRowOutcomeSchema.optional(),
  offset: intParam(0, 0, 1_000_000),
  limit: intParam(50, 1, 200),
});
export const ContactImportRowPageSchema = z.object({ rows: z.array(ContactImportRowSchema), rowsTotal: z.number().int(), offset: z.number().int(), limit: z.number().int() });

export const ApplyContactImportSchema = z.object({ confirm: z.literal(true) });

// ═════════════════════════ Campaigns (WA-08) ═════════════════════════

export const CAMPAIGN_STATES = ['draft', 'pending_approval', 'approved', 'scheduled', 'running', 'paused', 'completed', 'cancelled'] as const;
export const CampaignStateSchema = z.enum(CAMPAIGN_STATES);
export type CampaignState = z.infer<typeof CampaignStateSchema>;
export const CAMPAIGN_STATE_LABELS: Record<CampaignState, string> = {
  draft: 'Borrador',
  pending_approval: 'Pendiente de aprobación',
  approved: 'Aprobada',
  scheduled: 'Programada',
  running: 'Enviando',
  paused: 'En pausa',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

export const RECIPIENT_STATUSES = ['pending', 'excluded', 'queued', 'sent', 'delivered', 'read', 'replied', 'failed', 'skipped'] as const;
export const RecipientStatusSchema = z.enum(RECIPIENT_STATUSES);
export type RecipientStatus = z.infer<typeof RecipientStatusSchema>;
export const RECIPIENT_STATUS_LABELS: Record<RecipientStatus, string> = {
  pending: 'Por enviar',
  excluded: 'Excluido',
  queued: 'En envío',
  sent: 'Enviado',
  delivered: 'Entregado',
  read: 'Leído',
  replied: 'Respondió',
  failed: 'Falló',
  skipped: 'Omitido al enviar',
};

/** Why a contact of the segment does not receive the campaign (A25: the preview explains every exclusion). */
export const EXCLUSION_REASONS = [
  'invalid_number',
  'duplicate_identity',
  'suppressed',
  'permission_missing',
  'permission_denied',
  'test_mode',
  'disqualified',
  'human_owned',
  'active_call',
  'booked',
  'recently_contacted',
  'missing_variable',
] as const;
export const ExclusionReasonSchema = z.enum(EXCLUSION_REASONS);
export type ExclusionReason = z.infer<typeof ExclusionReasonSchema>;
export const EXCLUSION_REASON_LABELS: Record<ExclusionReason, string> = {
  invalid_number: 'Sin número de WhatsApp válido',
  duplicate_identity: 'Número duplicado (otro contacto del segmento ya lo tiene)',
  suppressed: 'Pidió no ser contactado (baja/supresión)',
  permission_missing: 'Sin permiso registrado de WhatsApp (un número importado no es permiso)',
  permission_denied: 'Permiso de WhatsApp negado o revocado',
  test_mode: 'Modo de prueba: el número no está en la lista permitida',
  disqualified: 'Descalificado',
  human_owned: 'Conversación atendida por un asesor',
  active_call: 'En una llamada activa',
  booked: 'Ya tiene una cita próxima',
  recently_contacted: 'Contactado recientemente',
  missing_variable: 'Faltan datos para personalizar la plantilla',
};

export const QUALIFICATION_OPS = ['equals', 'not_equals', 'contains', 'exists', 'not_exists'] as const;
export const QUALIFICATION_OP_LABELS: Record<(typeof QUALIFICATION_OPS)[number], string> = {
  equals: 'es igual a',
  not_equals: 'es distinto de',
  contains: 'contiene',
  exists: 'tiene un valor',
  not_exists: 'no tiene valor',
};

export const CampaignSegmentSchema = z.object({
  /** Empty = every project the creator can see (only allowed for members without project restrictions). */
  projectIds: z.array(Id).max(50).default([]),
  stages: z.array(z.string().min(1).max(80)).max(30).default([]),
  sources: z.array(z.string().min(1).max(120)).max(30).default([]),
  campaignIds: z.array(z.string().min(1).max(200)).max(30).default([]),
  /** Latest activity (last message of any conversation, else interest creation) before/after these instants. */
  lastActivityBefore: Iso.nullable().default(null),
  lastActivityAfter: Iso.nullable().default(null),
  qualification: z
    .array(z.object({ field: z.string().min(1).max(80), op: z.enum(QUALIFICATION_OPS), value: z.string().max(200).nullable().default(null) }))
    .max(10)
    .default([]),
  /** Business-initiated WhatsApp needs a granted permission: always required, never optional. */
  requireWhatsappPermission: z.literal(true).default(true),
  excludeHumanOwned: z.boolean().default(true),
  excludeBooked: z.boolean().default(true),
  /** Skip contacts that received any outbound WhatsApp in the last N days (null = do not apply). */
  excludeContactedWithinDays: z.number().int().min(1).max(365).nullable().default(7),
});
export type CampaignSegment = z.infer<typeof CampaignSegmentSchema>;

export const VARIABLE_SOURCES = ['contact.first_name', 'contact.name', 'project.name', 'interest.stage', 'qualification', 'text'] as const;
export const VariableSourceSchema = z.enum(VARIABLE_SOURCES);
export type VariableSource = z.infer<typeof VariableSourceSchema>;
export const VARIABLE_SOURCE_LABELS: Record<VariableSource, string> = {
  'contact.first_name': 'Nombre de pila del contacto',
  'contact.name': 'Nombre completo del contacto',
  'project.name': 'Nombre del proyecto',
  'interest.stage': 'Etapa del prospecto',
  qualification: 'Dato de calificación',
  text: 'Texto fijo',
};

export const CampaignVariableSchema = z.object({
  source: VariableSourceSchema,
  /** Fixed text (source=text) or the qualification field key (source=qualification). */
  value: z.string().max(200).nullable().default(null),
  /** Used when the contact has no value; empty → the contact is excluded (missing_variable). */
  fallback: z.string().max(200).nullable().default(null),
});
export type CampaignVariable = z.infer<typeof CampaignVariableSchema>;

export const LocalSendWindowSchema = z.object({ start: Hhmm, end: Hhmm });
export type LocalSendWindow = z.infer<typeof LocalSendWindowSchema>;

export const CampaignStopRulesSchema = z.object({
  /** Auto-pause when failed / (sent + failed) exceeds this after `minSample` attempts. */
  maxFailureRate: z.number().min(0.01).max(1).default(0.2),
  minSample: z.number().int().min(1).max(10_000).default(20),
});
export type CampaignStopRules = z.infer<typeof CampaignStopRulesSchema>;

/**
 * Limited follow-up for non-respondents (PRD §13 reactivation): at most 2 extra approved templates, never sooner
 * than 24 h after the previous message, only to recipients that received the campaign and have not replied. Every
 * exclusion (opt-out, suppression, permission, booking, human owner, schedule…) is re-checked at send time.
 */
export const FOLLOW_UP_MIN_HOURS = 24;
export const FOLLOW_UP_MAX_HOURS = 720;
export const FOLLOW_UP_MAX_COUNT = 2;
export const CampaignFollowUpSchema = z.object({
  enabled: z.boolean().default(false),
  afterHours: z.number().int().min(FOLLOW_UP_MIN_HOURS).max(FOLLOW_UP_MAX_HOURS).default(48),
  maxFollowUps: z.number().int().min(1).max(FOLLOW_UP_MAX_COUNT).default(1),
  templateName: z.string().min(1).max(512).nullable().default(null),
  templateLanguage: z.string().min(2).max(15).nullable().default(null),
  variables: z.record(z.string().max(60), CampaignVariableSchema).default({}),
});
export type CampaignFollowUp = z.infer<typeof CampaignFollowUpSchema>;

export const CampaignInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  segment: CampaignSegmentSchema.default(CampaignSegmentSchema.parse({})),
  templateName: z.string().min(1).max(512).nullable().default(null),
  templateLanguage: z.string().min(2).max(15).nullable().default(null),
  variables: z.record(z.string().max(60), CampaignVariableSchema).default({}),
  scheduledAt: Iso.nullable().default(null),
  localSendWindow: LocalSendWindowSchema.nullable().default(null),
  throttlePerMinute: z.number().int().min(1).max(1000).default(20),
  stopRules: CampaignStopRulesSchema.default({ maxFailureRate: 0.2, minSample: 20 }),
  followUp: CampaignFollowUpSchema.nullable().default(null),
});
export type CampaignInput = z.infer<typeof CampaignInputSchema>;
/** PATCH: only the fields sent change (no defaults applied to omitted fields). */
export const UpdateCampaignSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  segment: CampaignSegmentSchema.optional(),
  templateName: z.string().min(1).max(512).nullable().optional(),
  templateLanguage: z.string().min(2).max(15).nullable().optional(),
  variables: z.record(z.string().max(60), CampaignVariableSchema).optional(),
  scheduledAt: Iso.nullable().optional(),
  localSendWindow: LocalSendWindowSchema.nullable().optional(),
  throttlePerMinute: z.number().int().min(1).max(1000).optional(),
  stopRules: CampaignStopRulesSchema.optional(),
  followUp: CampaignFollowUpSchema.nullable().optional(),
});

const PersonRef = z.object({ userId: z.string(), name: z.string() }).nullable();
export const RecipientCountsSchema = z.object(Object.fromEntries(RECIPIENT_STATUSES.map((s) => [s, z.number().int()])) as Record<RecipientStatus, z.ZodNumber>);

export const CampaignSchema = z.object({
  id: Id,
  name: z.string(),
  state: CampaignStateSchema,
  segment: CampaignSegmentSchema,
  projectIds: z.array(z.string()),
  templateName: z.string().nullable(),
  templateLanguage: z.string().nullable(),
  variables: z.record(z.string(), CampaignVariableSchema),
  scheduledAt: Iso.nullable(),
  localSendWindow: LocalSendWindowSchema.nullable(),
  throttlePerMinute: z.number().int(),
  stopRules: CampaignStopRulesSchema,
  followUp: CampaignFollowUpSchema.nullable(),
  pauseReason: z.string().nullable(),
  approvalNote: z.string().nullable(),
  createdBy: PersonRef,
  approvedBy: PersonRef,
  approvedAt: Iso.nullable(),
  startedAt: Iso.nullable(),
  completedAt: Iso.nullable(),
  createdAt: Iso,
  updatedAt: Iso,
  version: z.number().int(),
  counts: RecipientCountsSchema,
  /** Whether the caller may approve (admin+, not the creator unless the workspace has a single admin/owner). */
  canApprove: z.boolean(),
  approveBlockedReason: z.string().nullable(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignSummarySchema = CampaignSchema.pick({ id: true, name: true, state: true, templateName: true, scheduledAt: true, startedAt: true, completedAt: true, createdAt: true, updatedAt: true, pauseReason: true, counts: true, projectIds: true });
export type CampaignSummary = z.infer<typeof CampaignSummarySchema>;

export const CampaignTemplateOptionSchema = z.object({
  connectionId: z.string(),
  name: z.string(),
  language: z.string(),
  category: z.string(),
  headerText: z.string().nullable(),
  bodyText: z.string(),
  footerText: z.string().nullable(),
  variables: z.array(z.object({ key: z.string(), component: z.enum(['header', 'body']), example: z.string().nullable() })),
  sendable: z.boolean(),
  unsendableReason: z.string().nullable(),
});
export type CampaignTemplateOption = z.infer<typeof CampaignTemplateOptionSchema>;

export const CampaignCapabilitiesSchema = z.object({
  /** At least one WhatsApp number verified for unattended sends. Campaigns can be drafted without it but not started. */
  whatsappReady: z.boolean(),
  whatsappReason: z.string().nullable(),
  templates: z.array(CampaignTemplateOptionSchema),
  providerMaxPerMinute: z.number().int(),
  maxRecipients: z.number().int(),
  testMode: z.boolean(),
  timezone: z.string(),
  canApprove: z.boolean(),
  singleApprover: z.boolean(),
});
export type CampaignCapabilities = z.infer<typeof CampaignCapabilitiesSchema>;

export const AudienceRowSchema = z.object({
  contactId: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  projectId: z.string().nullable(),
  projectName: z.string().nullable(),
  stage: z.string().nullable(),
  eligible: z.boolean(),
  reason: ExclusionReasonSchema.nullable(),
  /** Recipient status once the campaign started (snapshot); null in a live preview. */
  status: RecipientStatusSchema.nullable(),
  detail: z.string().nullable(),
});
export type AudienceRow = z.infer<typeof AudienceRowSchema>;

export const AudiencePreviewSchema = z.object({
  evaluatedAt: Iso,
  /** Live evaluation (draft) or the snapshot materialized when the campaign started. */
  source: z.enum(['live', 'snapshot']),
  total: z.number().int(),
  eligible: z.number().int(),
  excluded: z.number().int(),
  reasons: z.array(z.object({ reason: ExclusionReasonSchema, label: z.string(), count: z.number().int() })),
  rows: z.array(AudienceRowSchema),
  rowsTotal: z.number().int(),
  offset: z.number().int(),
  limit: z.number().int(),
  samples: z.array(z.object({ contactId: z.string(), name: z.string().nullable(), text: z.string() })),
  usage: z.object({
    meter: z.literal('whatsapp_templates'),
    templates: z.number().int(),
    /** Provider price per template is not known in the app: cost stays unknown (never shown as 0). */
    costKnown: z.literal(false),
    budget: z.object({ state: UsageBudgetStateSchema, used: z.number(), hardCap: z.number().nullable(), wouldExceed: z.boolean() }).nullable(),
    note: z.string(),
  }),
  maxRecipients: z.number().int(),
  overLimit: z.boolean(),
});
export type AudiencePreview = z.infer<typeof AudiencePreviewSchema>;

export const AudienceQuerySchema = z.object({
  filter: z.enum(['all', 'eligible', 'excluded']).default('all'),
  reason: ExclusionReasonSchema.optional(),
  offset: intParam(0, 0, 1_000_000),
  limit: intParam(25, 1, 100),
});

export const AudiencePreviewRequestSchema = z.object({
  segment: CampaignSegmentSchema,
  templateName: z.string().min(1).max(512).nullable().default(null),
  templateLanguage: z.string().min(2).max(15).nullable().default(null),
  variables: z.record(z.string().max(60), CampaignVariableSchema).default({}),
  filter: z.enum(['all', 'eligible', 'excluded']).default('all'),
  reason: ExclusionReasonSchema.optional(),
  offset: z.number().int().min(0).default(0),
  limit: z.number().int().min(1).max(100).default(25),
});

export const RejectCampaignSchema = z.object({ note: z.string().trim().min(1).max(500) });
export const PauseCampaignSchema = z.object({ reason: z.string().trim().max(300).nullable().default(null) });

export const CampaignReportSchema = z.object({
  campaignId: z.string(),
  state: CampaignStateSchema,
  generatedAt: Iso,
  counts: RecipientCountsSchema.extend({ total: z.number().int() }),
  exclusions: z.array(z.object({ reason: ExclusionReasonSchema, label: z.string(), count: z.number().int() })),
  failures: z.array(z.object({ reason: z.string(), count: z.number().int() })),
  skips: z.array(z.object({ reason: z.string(), count: z.number().int() })),
  stopRule: z.object({ attempts: z.number().int(), failed: z.number().int(), failureRate: z.number().nullable(), threshold: z.number(), minSample: z.number().int() }),
  replies: z.number().int(),
  /** Non-respondent follow-ups (null when the campaign has none configured). */
  followUps: z
    .object({
      maxFollowUps: z.number().int(),
      afterHours: z.number().int(),
      /** Follow-up messages accepted by the provider (sum over recipients). */
      sent: z.number().int(),
      /** Recipients that received at least one follow-up. */
      recipients: z.number().int(),
      /** Recipients with a follow-up that later replied. */
      repliedAfterFollowUp: z.number().int(),
      /** Follow-ups not sent because the recipient stopped being eligible (reason → count). */
      stopped: z.array(z.object({ reason: z.string(), count: z.number().int() })),
    })
    .nullable(),
  bookings: z.object({ count: z.number().int(), windowDays: z.number().int(), definition: z.string() }),
  definitions: z.record(z.string(), z.string()),
});
export type CampaignReport = z.infer<typeof CampaignReportSchema>;

export const CampaignReportQuerySchema = z.object({ attributionDays: intParam(14, 1, 90) });

export const CampaignRecipientSchema = z.object({
  id: z.string(),
  contactId: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  status: RecipientStatusSchema,
  reason: z.string().nullable(),
  dueAt: Iso.nullable(),
  sentAt: Iso.nullable(),
  conversationId: z.string().nullable(),
});
export const CampaignRecipientsQuerySchema = z.object({ status: RecipientStatusSchema.optional(), offset: intParam(0, 0, 1_000_000), limit: intParam(50, 1, 200) });
export const CampaignRecipientPageSchema = z.object({ items: z.array(CampaignRecipientSchema), total: z.number().int(), offset: z.number().int(), limit: z.number().int() });
